/* eslint-disable import/no-internal-modules -- reuses the event page's pill, model and check-in hook */
import { Loading } from '@livecontrol/core-ui';
import { Str } from '@livecontrol/core-utils';
import { DateTime } from 'luxon';
import type { ReactElement, ReactNode } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Tag, Tooltip } from '../../components';
import { Store } from '../../store';
import { useCheckinEvent } from '../event-details';
import { CopyPill } from '../event-details-v2/copy-pill';
import { eventTypeInfo, STATUS } from '../events/event-model';
import { EventCommands } from './commands';
import { useLiveLiteEvent } from './use-live-lite-event';
import type { LiveLiteEventDetails } from './use-live-lite-event';
import { webplayerLink } from './webplayer-link';

const LIST_ROUTE = '/lite/live';

const Page = styled.div`
  max-width: 900px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 2px;

  .back {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: var(--white40);
    font-size: 14px;
    font-weight: 500;
    transition: color var(--transition);

    &:hover,
    &:focus {
      outline: none;
      color: var(--white);
    }
  }

  .tz {
    color: var(--white40);
    font-size: 13px;
    white-space: nowrap;

    b {
      font-weight: 700;
    }
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--white);
  }
`;

const Pills = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  margin-bottom: 15px;
`;

const WebplayerButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(213, 221, 238, 0.92);
  font-size: 12px;
  font-weight: 600;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    text-decoration: none;
    color: var(--white);
    background: rgba(255, 255, 255, 0.06);
  }

  &.disabled {
    opacity: 0.45;
    pointer-events: none;
  }
`;

const FullPageLink = styled(Link)`
  color: var(--white40);
  font-size: 11px;

  &:hover,
  &:focus {
    color: var(--white);
  }
`;

/* Same card as the event page's info grid (event-details-v2/event-info.tsx). */
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px 18px;
  padding: 15px 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);
  margin-bottom: 15px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr 1fr;
  }

  .entry {
    min-width: 0;
  }

  .entry .title {
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.55);
    margin-bottom: 7px;
  }

  .entry .text {
    color: var(--white);
    font-weight: 600;
    font-size: 13px;
  }

  .separator {
    grid-column: 1 / -1;
    height: 1px;
    background: rgba(255, 255, 255, 0.07);
    margin: 1px 0px;
  }

  .people {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 8px 10px;
    flex-wrap: wrap;
  }
`;

const Capsule = styled.button<{ $active: boolean; $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px 5px 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  appearance: none;
  font: inherit;
  cursor: ${({ $clickable }): string => ($clickable ? 'pointer' : 'default')};
  transition: border-color var(--transition);

  ${({ $clickable }): string =>
    $clickable ? '&:hover, &:focus { outline: none; border-color: rgba(255, 255, 255, 0.22); }' : ''}

  .circle {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    background: ${({ $active }): string =>
      $active ? 'rgba(53, 211, 153, 0.12)' : 'rgba(255, 255, 255, 0.05)'};
    border: 1px solid
      ${({ $active }): string => ($active ? 'rgba(53, 211, 153, 0.55)' : 'rgba(255, 255, 255, 0.1)')};
    color: ${({ $active }): string => ($active ? 'rgb(53, 211, 153)' : 'rgba(176, 186, 209, 0.7)')};
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 5px;
    text-align: left;
  }

  .role {
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.45);
    font-weight: 600;
    font-size: 8px;
  }

  .name {
    color: rgba(213, 221, 238, 0.92);
    white-space: nowrap;
    font-weight: 600;
    font-size: 11px;
  }
`;

const Flags = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 15px;
`;

const PriorityNote = styled.div`
  padding: 11px 14px;
  border-radius: 11px;
  border: 1px solid rgba(255, 80, 95, 0.35);
  background: rgba(255, 80, 95, 0.08);
  color: rgba(213, 221, 238, 0.92);
  font-size: 13px;
  margin-bottom: 15px;
`;

const Entry = ({ title, children }: { title: string; children: ReactNode }): ReactElement => (
  <div className='entry'>
    <div className='title'>{title}</div>
    {children}
  </div>
);

const EventBody = ({
  event,
  refetch
}: {
  event: LiveLiteEventDetails;
  refetch: () => Promise<unknown>;
}): ReactElement => {
  const { me } = Store.User.useIdentity();
  const [checkinEvent] = useCheckinEvent();

  const start = DateTime.fromISO(event.startTime ?? '');
  const end = DateTime.fromISO(event.endTime ?? '');

  // Browser time zone, like the rest of the Command Center.
  const timeText = start.isValid ? `${start.toFormat('h:mm')} – ${end.toFormat('h:mm a')}` : '—';
  const date = start.isValid ? start.toLocaleString(DateTime.DATE_SHORT) : '—';

  const status = event.isLive ? 'live' : start > DateTime.now() ? 'upcoming' : 'past';
  const badge = STATUS[status];

  const checkedIn = !!event.checked_user;
  const link = webplayerLink(event);

  /* Support check-in writes only to our database, so it stays on a page that avoids other services. */
  const toggleCheckin = (): void => {
    void checkinEvent({
      eventId: event.eid,
      locationId: event.location ?? undefined,
      checkedUser: !checkedIn && me?.id ? Number(me.id) : undefined
    }).then(refetch);
  };

  return (
    <>
      <Title>
        <h1>{event.name ?? 'Untitled'}</h1>
        <Tag size='small' className={badge.className} icon={badge.icon}>
          {badge.label}
        </Tag>
      </Title>

      <Pills>
        <CopyPill label='CID' value={`${event.client?.cid ?? ''}.${event.location_secondary_id ?? ''}`} />
        <CopyPill label='EID' value={event.eid} />

        <Tooltip content={link.tooltip} placement='bottom-left'>
          <WebplayerButton
            className={link.url ? undefined : 'disabled'}
            href={link.url}
            target='_blank'
            rel='noreferrer'
          >
            <i className={link.hidden ? 'far fa-eye-slash' : 'fal fa-play-circle'} />
            Webplayer
          </WebplayerButton>
        </Tooltip>

        {/* The full page resolves the link through the webplayer backend, so it works when this cannot. */}
        {!link.url && <FullPageLink to={`/event/${event.eid}`}>Full event page</FullPageLink>}
      </Pills>

      <Grid>
        <Entry title='Client'>
          <span className='text'>{event.client?.organizationName ?? '—'}</span>
        </Entry>

        <Entry title='Location'>
          <span className='text'>{event.location_name ?? '—'}</span>
        </Entry>

        <Entry title='Event Type'>
          <span className='text'>{eventTypeInfo(event.eventType ?? undefined).label}</span>
        </Entry>

        <Entry title='Time'>
          <span className='text'>{timeText}</span>
        </Entry>

        <Entry title='Date'>
          <span className='text'>{date}</span>
        </Entry>

        <Entry title='Password'>
          {event.event_password ? (
            <CopyPill value={event.event_password} />
          ) : (
            <span className='text'>—</span>
          )}
        </Entry>

        <div className='separator' />

        <div className='people'>
          <Tooltip
            content={checkedIn ? `Checked in by ${event.checked_user ?? 'user'}` : 'Check in'}
            placement='bottom-left'
          >
            <Capsule
              type='button'
              $active={checkedIn}
              $clickable
              onClick={(clickEvent): void => {
                // Drop focus so the tooltip doesn't linger via :focus-within after a click.
                clickEvent.currentTarget.blur();
                toggleCheckin();
              }}
            >
              <span className='circle'>
                <i className={checkedIn ? 'far fa-check' : 'far fa-user-headset'} />
              </span>
              <span className='info'>
                <span className='role'>Support</span>
                <span className='name'>{event.checked_user ?? event.verifier_email ?? '—'}</span>
              </span>
            </Capsule>
          </Tooltip>

          <Tooltip
            content={event.operator_name ? 'Videographer assigned' : 'No videographer assigned'}
            placement='bottom-left'
          >
            <Capsule type='button' $active={false} $clickable={false}>
              <span className='circle'>
                <i className={event.operator_name ? 'far fa-video' : 'far fa-minus'} />
              </span>
              <span className='info'>
                <span className='role'>Videographer</span>
                <span className='name'>{event.operator_name ?? '—'}</span>
              </span>
            </Capsule>
          </Tooltip>
        </div>
      </Grid>

      {(!!event.receive_iso_recordings || !!event.audio_cue_sheet_url) && (
        <Flags>
          {!!event.receive_iso_recordings && (
            <Tag size='small' className='cc-neutral'>
              ISO
            </Tag>
          )}
          {!!event.audio_cue_sheet_url && (
            <Tag size='small' className='cc-neutral'>
              Audio Mix
            </Tag>
          )}
        </Flags>
      )}

      {!!event.high_priority && (
        <PriorityNote>
          <i className='fas fa-exclamation-triangle' style={{ marginRight: 8, color: 'rgb(255, 138, 147)' }} />
          {event.high_priority_note ?? 'High priority event.'}
        </PriorityNote>
      )}

      <EventCommands event={event} />
    </>
  );
};

/*
 * One event, basic information and the lifecycle / danger commands only. No video, no webplayer link and
 * no tabs: nothing here is fetched from the webplayer backend, the asset service or Simulcasting.
 * Reached by URL, `/lite/<event id>`.
 */
export const LiveLiteEvent = (): ReactElement => {
  const history = useHistory();
  const eid = Str.normalize(useParams<{ eid: string }>().eid) ?? '';
  const { event, loading, error, refetch } = useLiveLiteEvent(eid);

  let content: ReactElement;

  if (loading && !event) {
    content = <Loading isOldVersion />;
  } else if (error) {
    content = (
      <Panel className='text-center p-16px text-danger'>
        Error trying to load the event.{' '}
        <button
          type='button'
          className='x-pseudo text-white text-decoration-underline'
          onClick={(): void => {
            void refetch();
          }}
        >
          Click Here to retry
        </button>
      </Panel>
    );
  } else if (!event) {
    content = <Panel className='text-center p-16px text-white'>Event {eid} not found.</Panel>;
  } else {
    content = <EventBody event={event} refetch={refetch} />;
  }

  return (
    <Page>
      <TopBar>
        <button
          type='button'
          className='x-pseudo back'
          onClick={(): void => {
            history.push(LIST_ROUTE);
          }}
        >
          <i className='far fa-arrow-left' /> Back to Live Events
        </button>

        <div className='tz'>
          <b>Time Zone:</b> {DateTime.local().toFormat('z')}
        </div>
      </TopBar>

      {content}
    </Page>
  );
};
